import React, { useState, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle, Upload, FileText, X } from 'lucide-react'

export default function Contact() {
  const location = useLocation()
  const initialService = location.state?.service || ''

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: initialService || 'Indendørs Maling',
    message: '',
    website_url: '' // Honeypot field
  })

  const [files, setFiles] = useState([])
  const [fileError, setFileError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null) // 'success' or 'error'
  const [submitMessage, setSubmitMessage] = useState('')
  
  const fileInputRef = useRef(null)

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e) => {
    setFileError('')
    const selectedFiles = Array.from(e.target.files)
    
    // Validation limits
    const maxFiles = 5
    const maxSingleSize = 10 * 1024 * 1024 // 10 MB
    const maxTotalSize = 20 * 1024 * 1024 // 20 MB
    const allowedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp']

    if (files.length + selectedFiles.length > maxFiles) {
      setFileError(`Du kan højst uploade ${maxFiles} filer i alt.`)
      return
    }

    let currentTotalSize = files.reduce((sum, f) => sum + f.size, 0)
    const validFiles = []

    for (const file of selectedFiles) {
      if (!allowedTypes.includes(file.type)) {
        setFileError(`Filtypen "${file.name}" er ikke tilladt. Kun PDF, PNG, JPEG, GIF og WEBP understøttes.`)
        return
      }
      if (file.size > maxSingleSize) {
        setFileError(`Filen "${file.name}" er for stor. Maksimal filstørrelse er 10 MB.`)
        return
      }
      if (currentTotalSize + file.size > maxTotalSize) {
        setFileError(`Den samlede filstørrelse overstiger grænsen på 20 MB.`)
        return
      }
      currentTotalSize += file.size
      validFiles.push(file)
    }

    setFiles(prev => [...prev, ...validFiles])
  }

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    setSubmitStatus(null)
    setSubmitMessage('')

    // Prepare FormData
    const form = e.target
    const data = new FormData()
    
    // Append standard fields
    data.append('name', formData.name)
    data.append('email', formData.email)
    data.append('phone', formData.phone)
    data.append('subject', formData.subject)
    data.append('message', formData.message)
    data.append('website_url', formData.website_url) // Honeypot

    // Append files
    files.forEach((file) => {
      data.append('attachments', file)
    })

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: data
        // Note: Content-Type header is omitted so the browser sets the multipart boundary automatically
      })

      const result = await response.json()

      if (response.ok && result.status === 'ok') {
        setSubmitStatus('success')
        setSubmitMessage('Mange tak for din henvendelse! Vi har modtaget din besked og vender tilbage med et uforpligtende tilbud inden for 24 timer.')
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: 'Indendørs Maling',
          message: '',
          website_url: ''
        })
        setFiles([])
      } else {
        setSubmitStatus('error')
        setSubmitMessage(result.message || 'Der opstod en fejl under indsendelsen af formularen. Prøv venligst igen eller ring til os direkte.')
      }
    } catch (error) {
      console.error('Form submission error:', error)
      setSubmitStatus('error')
      setSubmitMessage('Der opstod en netværksfejl. Tjek venligst din internetforbindelse og prøv igen.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      {/* Main Grid */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-5">
            {/* Left Column: Contact Details (2 cols) */}
            <div className="lg:col-span-2 space-y-8">
              <div className="space-y-6">
                <h2 className="text-2xl font-semibold text-slate-900">Vores Kontaktoplysninger</h2>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Du er altid velkommen til at ringe eller skrive til os. Vi bestræber os på at besvare alle skriftlige henvendelser inden for 24 timer på hverdage.
                </p>

                <div className="divide-y divide-slate-200 pt-4">
                  <div className="flex items-start gap-4 pb-5">
                    <div className="text-black shrink-0 pt-1">
                      <Phone className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm">Telefon</h3>
                      <a href="tel:+4592900091" className="text-black hover:underline font-semibold text-base mt-0.5 block">
                        +45 92 90 00 91
                      </a>
                      <p className="text-xs text-slate-500 mt-0.5">Man - Fre: 07:00 - 16:00</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 py-5">
                    <div className="text-black shrink-0 pt-1">
                      <Mail className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm">E-mail</h3>
                      <a href="mailto:info@probyg.tech" className="text-black hover:underline font-semibold text-base mt-0.5 block">
                        info@probyg.tech
                      </a>
                      <p className="text-xs text-slate-500 mt-0.5">Vi svarer hurtigt på alle mails</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 py-5">
                    <div className="text-black shrink-0 pt-1">
                      <MapPin className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm">Adresse</h3>
                      <p className="text-slate-700 text-sm mt-0.5">
                        Njalsgade 21F 2.,<br />
                        2300 København S
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 pt-5">
                    <div className="text-black shrink-0 pt-1">
                      <Clock className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm">Åbningstider</h3>
                      <p className="text-slate-700 text-sm mt-0.5">Mandag - Fredag: 07:00 - 16:00</p>
                      <p className="text-slate-700 text-sm">Lørdag - Søndag: Lukket</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map Box */}
              <div className="rounded-2xl border border-slate-200 overflow-hidden shadow-sm bg-white h-80 relative flex flex-col justify-end">
                {/* Interactive Google Maps */}
                <div className="absolute inset-0 w-full h-full">
                  <iframe
                    src="https://maps.google.com/maps?q=Njalsgade%2021F%202.,%202300%20K%C3%B8benhavn%20S&t=&z=14&ie=UTF8&iwloc=&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    title="Probyg Maler lokation"
                    className="w-full h-full grayscale opacity-85 hover:grayscale-0 transition-all duration-300"
                  ></iframe>
                </div>
                {/* Map styled overlay */}
                <div className="relative z-10 bg-white/95 backdrop-blur-sm p-4 border-t border-slate-200 text-xs text-center text-slate-700 font-medium">
                  📍 Njalsgade 21F 2., 2300 København S — Vi dækker hele Sjælland!
                </div>
              </div>
            </div>

            {/* Right Column: Contact Form (3 cols) */}
            <div className="lg:col-span-3">
              <div className="rounded-2xl bg-white border border-slate-200 p-8 shadow-sm space-y-6">
                <div className="space-y-2">
                  <h2 className="text-2xl font-semibold text-slate-900">Få et gratis & uforpligtende tilbud</h2>
                  <p className="text-slate-600 text-sm">
                    Udfyld formularen herunder med detaljer om din opgave. Vedhæft gerne billeder af de vægge, rum eller facader, der skal males, så kan vi give dig et mere præcist tilbud hurtigere.
                  </p>
                </div>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="rounded-xl bg-slate-50 border border-slate-200 p-5 flex gap-3.5 text-slate-800">
                    <CheckCircle className="h-6 w-6 text-black shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-sm">Besked sendt!</h4>
                      <p className="text-xs mt-1 leading-relaxed">{submitMessage}</p>
                    </div>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="rounded-xl bg-rose-50 border border-rose-200 p-5 flex gap-3.5 text-rose-800">
                    <AlertCircle className="h-6 w-6 text-rose-600 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-sm">Der opstod en fejl</h4>
                      <p className="text-xs mt-1 leading-relaxed">{submitMessage}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
                  <div className="sr-only absolute -top-96 -left-96">
                    <label htmlFor="website_url">Do not fill this field if you are human</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* Name & Email Grid */}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Dit Navn <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="F.eks. Anders Nielsen"
                        className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Din E-mail <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="F.eks. anders@mail.dk"
                        className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      />
                    </div>
                  </div>

                  {/* Phone & Subject Grid */}
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <label htmlFor="phone" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Telefonnummer <span className="text-rose-500">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="F.eks. 92 90 00 91"
                        className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="subject" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Hvad skal males? <span className="text-rose-500">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-900 bg-white focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                      >
                        <option value="Indendørs Maling">Indendørs Maling</option>
                        <option value="Udendørs Maling">Udendørs Maling</option>
                        <option value="Tapetsering & Filt">Tapetsering & Filt</option>
                        <option value="Erhvervsmaling">Erhvervsmaling</option>
                        <option value="Andet / Ved ikke">Andet / Ved ikke</option>
                      </select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <label htmlFor="message" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                      Beskrivelse af opgaven <span className="text-rose-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Beskriv opgaven så godt som muligt. F.eks. antal rum, kvadratmeter, nuværende stand, og om der skal fuldspartles eller opsættes filt."
                      className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-black focus:outline-none focus:ring-1 focus:ring-black"
                    />
                  </div>

                  {/* File Upload Attachment */}
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-slate-700 block">
                      Vedhæft billeder eller filer (valgfrit)
                    </label>
                    
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center cursor-pointer hover:border-black hover:bg-slate-50 transition-all"
                    >
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        multiple
                        accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                        className="hidden"
                      />
                      <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-slate-700">Klik for at vælge filer</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Tilladte formater: PDF, PNG, JPEG, GIF, WEBP (maks. 5 filer, 20 MB i alt)
                      </p>
                    </div>

                    {fileError && (
                      <p className="text-xs font-semibold text-rose-600 flex items-center gap-1 mt-1">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                        {fileError}
                      </p>
                    )}

                    {/* Selected Files List */}
                    {files.length > 0 && (
                      <div className="mt-3 space-y-2">
                        <p className="text-xs font-bold text-slate-700">Valgte filer ({files.length}):</p>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          {files.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between rounded-lg bg-slate-100 p-2.5 text-xs text-slate-700 border border-slate-200">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                                <span className="truncate font-medium">{file.name}</span>
                                <span className="text-slate-400 shrink-0">({(file.size / (1024 * 1024)).toFixed(2)} MB)</span>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeFile(idx)}
                                className="rounded-full p-1 hover:bg-slate-200 text-slate-500 hover:text-slate-900 transition-colors"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Submit Button */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex w-full items-center justify-center rounded-full bg-black px-6 py-3.5 text-base font-semibold text-white shadow-sm hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <div className="mr-2 h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Indsender tilbud...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          Send tilbudsforespørgsel
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
